import {
    MetadataViewProps, NestedMetadataViewProps,
} from '../types'

import {
    buttonContainer, item, itemContent, keyIcon, keyWrapper, metadataContainer,
    metadataWrapper, nestedWrapper, valueWrapper,
} from '../styles/AudienceMetadata'

import {CopyButton} from './CopyButton'
import {LockClosedIconSolid} from 'components/Icon'
import React from 'react'
import {TFunction} from 'i18next'

import {styled} from 'stitches/config'
import {toast} from 'components/Toast'
import {useCopyToClipboard} from 'hooks/clipboard'
import {useTranslation} from 'react-i18next'


const StyledMetadataContainer = styled('div', metadataContainer)
const StyledButtonContainer = styled('div', buttonContainer)
const StyledNested = styled('div', nestedWrapper)
const StyledValue = styled('div', valueWrapper)
const StyledItem = styled('div', {
    ...item,
    variants: {parent: {true: {[`& ${StyledValue}`]: {'&:hover': {backgroundColor: '$metadataBackground'}}}}},
})
const StyledItemContent = styled('div', itemContent)
const StyledMetadataView = styled('div', {
    ...metadataWrapper,
    '&:hover': {
        [`& ${StyledButtonContainer}`]: {visibility: 'visible'},
        [`& + ${StyledNested}`]: {
            backgroundColor: '$metadataItemBackgroundHover',
            borderRadius: '$metadataItemBorder',
        },
    },
})
const StyledKey = styled('div', keyWrapper)
const StyledKeyIcon = styled('div', keyIcon)

export const getPrettyKey = (key: string, t: TFunction) => {
    switch (key) {
        case 'Age':
            return t('Age')
        case 'Date of birth':
            return t('Date of birth')
        case 'Given name':
            return t('Given name')
        case 'First name':
            return t('First name')
        case 'Last name':
            return t('Last name')
        case 'Personal identity code':
            return t('SSN')
        case 'Roles':
            return t('Roles')
        default:
            return key
    }
}

const MetadataItemView = ({
    compact,
    currentKey,
    currentValue,
    metadataType,
    level,
}: MetadataViewProps): React.ReactElement => {
    const {t} = useTranslation()

    const clipboard = useCopyToClipboard({
        onSuccess: (key) => {
            toast.success(t('Copied: {{key}}', {key}))
        },
    })

    const handleCopySelected = () => {
        if (typeof currentValue === 'object' || window.getSelection()?.type === 'Range') {
            // Skip if parent or if user has selected text
            return
        }
        clipboard.copy(currentValue, currentKey)
    }

    return (
        <StyledMetadataView>
            <StyledItem parent={typeof currentValue === 'object'}>
                <StyledItemContent compact={compact}>
                    <StyledKey style={{width: `calc(160px - ${level * 1.4}rem)`}}>
                        <span data-testid='metadataKey'>{getPrettyKey(currentKey, t)}</span>
                        <StyledKeyIcon type={metadataType}>
                            <LockClosedIconSolid />
                        </StyledKeyIcon>
                    </StyledKey>
                    {typeof currentValue !== 'object' &&
                    <StyledValue
                        onClick={handleCopySelected}
                    >
                        {currentValue}
                    </StyledValue>}
                </StyledItemContent>
            </StyledItem>
            <StyledButtonContainer>
                <CopyButton currentKey={getPrettyKey(currentKey, t)} currentValue={currentValue} />
            </StyledButtonContainer>
        </StyledMetadataView>
    )
}

export const MetadataView = ({
    compact,
    metadata,
    metadataType,
    level,
}: NestedMetadataViewProps): React.ReactElement => (
    <StyledMetadataContainer>
        {
            typeof metadata === 'object' && metadata !== null && Object.entries(metadata)
                .map(([key, value]) => (
                    <div key={key}>
                        <MetadataItemView
                            compact={compact}
                            currentKey={key}
                            currentValue={value}
                            level={level}
                            metadataType={metadataType}
                        />
                        {typeof value === 'object' && value !== null && (
                            <StyledNested>
                                <MetadataView compact={compact} level={level + 1} metadata={value} metadataType={metadataType} />
                            </StyledNested>
                        )}
                    </div>
                ))
        }
    </StyledMetadataContainer>
)
